import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { clearLastRoute } from "@/native/useLastRoute";
import { capacitorStorage } from "@/native/storage";
import type { PrimaryPersona } from "@/api/me";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

/**
 * MV2-102 / MV2-907 — pending Cognito challenge surfaced after a valid
 * password but before sign-in completes. The LoginPage is a step machine
 * on this value (canvas 01b `auth/LoginMfa`, `auth/LoginNewPassword`):
 *
 *   `totp`          SOFTWARE_TOKEN_MFA — six-digit authenticator code,
 *                   completed by `confirmMfaCode`.
 *   `new_password`  NEW_PASSWORD_REQUIRED — the administrator set a
 *                   temporary password; completed by `completeNewPassword`.
 *                   Cognito may follow it with the TOTP step.
 *
 * `MFA_SETUP` and anything else still throw a sentence the page shows.
 */
export type PendingMfaChallenge = { kind: "totp" } | { kind: "new_password" };

/**
 * Impersonation context — a SuperAdmin "acting as" a client tenant. When
 * set, the UI:
 *   - filters list pages (`/admin/customers`, `/admin/sites`, etc.) to
 *     rows owned by `actingAsClientId`
 *   - pre-fills the client field on create forms
 *   - swaps BrandingProvider to the target client's theme
 *   - shows the ImpersonationBanner in the header
 *
 * Persisted in localStorage so a page reload keeps the context (cleared
 * on sign-out). Non-SuperAdmin users never set this; authStore enforces at
 * the caller level (see src/components/layout/TenantSwitcher.tsx).
 */
export interface ImpersonationContext {
  clientId: string;
  clientName: string;
  clientSlug: string;
}

/**
 * Persona preview — render-layer override for the SuperAdmin so they can
 * see /getting-started as if they were O&M / ClientAdmin / Site Owner /
 * SuperAdmin without changing accounts. Composes with
 * ``actingAs`` independently — SuperAdmin can preview a persona inside
 * an impersonation context, or on its own.
 *
 * Preview does NOT change permissions, route guards, or API responses
 * — only the GettingStartedPage checklist + welcome modal copy + tour
 * launcher honor it. Backend's /me/onboarding-progress accepts the same
 * value as ``preview_persona`` query param so the checklist swaps too.
 *
 * Persisted in localStorage so a page reload keeps the preview (cleared
 * on sign-out). Non-SuperAdmin senders are silently ignored at the setter,
 * matching the backend's defence-in-depth gate.
 */
export type PreviewPersona = "superadmin" | "clientadmin" | "om" | "siteowner";

/**
 * MV2-1250 — "View as" a real user (SuperAdmin only, backend.md §108).
 * The interceptor sends `X-View-As-User`; the backend resolves every
 * request as that user (role, policies, sites), read-only. The user
 * belongs to the Acting-as client, so changing `actingAs` clears it.
 */
export interface ViewingAs {
  userId: string;
  name: string;
  persona: PrimaryPersona | null;
  /**
   * MV2-1254 — what "Stop viewing as" restores: the Acting as you had before
   * (null = none) and the page you started from.
   */
  prevActingAs?: ImpersonationContext | null;
  returnTo?: string;
}

interface AuthState {
  status: AuthStatus;
  /** Cognito `sub` — stable user id, matches `users.id` in RDS. */
  sub: string | null;
  /** Cognito email claim; cosmetic pre-/me-hydration. */
  email: string | null;
  /** Groups from the Cognito ID token (`cognito:groups` claim). */
  groups: string[];
  /** Active impersonation context (SuperAdmin only). */
  actingAs: ImpersonationContext | null;
  /** Active persona preview (SuperAdmin only). */
  previewPersona: PreviewPersona | null;
  /** MV2-1250: the real user being viewed as (SuperAdmin only). */
  viewingAs: ViewingAs | null;
  /**
   * MV2-102: pending Cognito MFA challenge after a valid password.
   * `null` until `signInWithCredentials` lands on the TOTP step;
   * cleared once `confirmMfaCode` completes (or sign-out / abandon).
   */
  pendingMfaChallenge: PendingMfaChallenge | null;

  /** Called once at boot to check for a persisted session. */
  initialize: () => Promise<void>;
  /** Email + password → Cognito SRP. */
  signInWithCredentials: (email: string, password: string) => Promise<void>;
  /**
   * MV2-102: complete the pending TOTP challenge with the 6-digit
   * code from the user's authenticator app. Throws on wrong code so
   * the LoginPage can surface a friendly retry message.
   */
  confirmMfaCode: (code: string) => Promise<void>;
  /**
   * MV2-907: complete the NEW_PASSWORD_REQUIRED challenge with the
   * user's own password. Resolves to `"totp"` when Cognito then asks for
   * the authenticator code, otherwise the session is signed in.
   */
  completeNewPassword: (newPassword: string) => Promise<"signed_in" | "totp">;
  /** MV2-102: drop the pending challenge (e.g. user clicks Cancel). */
  cancelMfaChallenge: () => void;
  /** Sign out + clear Amplify + our own slice. */
  signOutAll: () => Promise<void>;
  /** Enter impersonation context (noop for non-SuperAdmin). */
  setActingAs: (ctx: ImpersonationContext | null) => void;
  /** Set persona preview (noop for non-SuperAdmin). */
  setPreviewPersona: (p: PreviewPersona | null) => void;
  /** MV2-1250: view as a real user (noop for non-SuperAdmin). */
  setViewingAs: (v: ViewingAs | null) => void;
}

async function readClaimsFromSession() {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;
  if (!idToken) return null;
  const payload = idToken.payload;
  return {
    sub: (payload.sub as string | undefined) ?? null,
    email: (payload.email as string | undefined) ?? null,
    groups: (payload["cognito:groups"] as string[] | undefined) ?? [],
  };
}

type SignInOutcome = "signed_in" | "totp" | "new_password";

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      /**
       * Map one Amplify sign-in result onto the store. Signed in → hydrate
       * claims; a known challenge → stash it (status stays unauthenticated,
       * the LoginPage pivots on `pendingMfaChallenge`); anything else →
       * throw a sentence the page can show.
       */
      const settle = async (
        result: { isSignedIn: boolean; nextStep?: { signInStep?: string } },
        email: string,
      ): Promise<SignInOutcome> => {
        if (result.isSignedIn) {
          const claims = await readClaimsFromSession();
          set({
            status: "authenticated",
            sub: claims?.sub ?? null,
            email: claims?.email ?? email,
            groups: claims?.groups ?? [],
            pendingMfaChallenge: null,
          });
          return "signed_in";
        }
        const step = result.nextStep?.signInStep;
        if (step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          // Stash the email so the TOTP board can say who is signing in;
          // status stays "unauthenticated" until the code lands.
          set({ email, pendingMfaChallenge: { kind: "totp" } });
          return "totp";
        }
        if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          set({ email, pendingMfaChallenge: { kind: "new_password" } });
          return "new_password";
        }
        throw new Error(
          step === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" ||
            step === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION"
            ? "Two-step verification isn't set up for this account yet. Ask your administrator to finish setting it up, then sign in again."
            : "This account needs a step we can't complete here. Ask your administrator.",
        );
      };

      return {
        status: "loading",
        sub: null,
        email: null,
        groups: [],
        actingAs: null,
        previewPersona: null,
        viewingAs: null,
        pendingMfaChallenge: null,

        initialize: async () => {
          try {
            const claims = await readClaimsFromSession();
            if (!claims || !claims.sub) {
              set({
                status: "unauthenticated",
                sub: null,
                email: null,
                groups: [],
                actingAs: null,
                previewPersona: null,
                viewingAs: null,
                pendingMfaChallenge: null,
              });
              return;
            }
            set({
              status: "authenticated",
              sub: claims.sub,
              email: claims.email,
              groups: claims.groups,
              pendingMfaChallenge: null,
            });
          } catch {
            set({
              status: "unauthenticated",
              sub: null,
              email: null,
              groups: [],
              actingAs: null,
              previewPersona: null,
              viewingAs: null,
              pendingMfaChallenge: null,
            });
          }
        },

        signInWithCredentials: async (email, password) => {
          // Amplify signIn — handles SRP + challenge plumbing. A successful
          // call returns { isSignedIn, nextStep }; `settle` maps the next
          // step onto the pending challenge the LoginPage renders.
          const result = await signIn({ username: email, password });
          await settle(result, email);
        },

        confirmMfaCode: async (code) => {
          // MV2-102: complete the TOTP challenge. Cognito throws
          // `CodeMismatchException` on wrong code — let it propagate so
          // the LoginPage can render a retry message; clear the pending
          // challenge only on success.
          const result = await confirmSignIn({ challengeResponse: code });
          const outcome = await settle(result, get().email ?? "");
          if (outcome !== "signed_in") {
            throw new Error(
              `Sign-in requires extra step: ${result.nextStep?.signInStep ?? "unknown"}`,
            );
          }
        },

        completeNewPassword: async (newPassword) => {
          // MV2-907: NEW_PASSWORD_REQUIRED. Cognito raises
          // `InvalidPasswordException` when the pool policy rejects it —
          // propagate so the page can say so under the field.
          const result = await confirmSignIn({
            challengeResponse: newPassword,
          });
          const outcome = await settle(result, get().email ?? "");
          if (outcome === "new_password") {
            throw new Error(
              "Sign-in requires extra step: NEW_PASSWORD_REQUIRED",
            );
          }
          return outcome;
        },

        cancelMfaChallenge: () => {
          set({ pendingMfaChallenge: null });
        },

        signOutAll: async () => {
          // Revoke the FCM device registration BEFORE Amplify signOut so the
          // Cognito token is still valid for the DELETE /me/devices/{id} call.
          // Dynamically imported so the native push module is never bundled
          // into the web/admin build.
          try {
            const { teardownPush } = await import("@/native/notifications");
            await teardownPush();
          } catch {
            // Non-fatal — don't block sign-out if push teardown fails.
          }

          await signOut();
          // Clear the saved route so a fresh login always lands on the
          // persona-default home, not on the previous user's last page.
          clearLastRoute();
          set({
            status: "unauthenticated",
            sub: null,
            email: null,
            groups: [],
            actingAs: null,
            previewPersona: null,
            viewingAs: null,
            pendingMfaChallenge: null,
          });
        },

        setActingAs: (ctx) => {
          // Only SuperAdmin can impersonate a client tenant. Silently ignore
          // for anyone else — the UI surfaces (TenantSwitcher) gate the call
          // too, this is belt-and-braces.
          const { groups } = get();
          if (!groups.includes("SuperAdmin")) return;
          // MV2-1250: the viewed user belongs to the previous client.
          if (ctx?.clientId !== get().actingAs?.clientId) {
            set({ actingAs: ctx, viewingAs: null });
          } else {
            set({ actingAs: ctx });
          }
        },

        setPreviewPersona: (p) => {
          // Persona preview is render-layer only and SuperAdmin-gated to
          // match the backend's `?preview_persona=` validation. Drop the
          // call silently for anyone else.
          const { groups } = get();
          if (!groups.includes("SuperAdmin")) return;
          set({ previewPersona: p });
        },

        setViewingAs: (v) => {
          // Backend honours X-View-As-User for SuperAdmin only (§108).
          const { groups } = get();
          if (!groups.includes("SuperAdmin")) return;
          set({ viewingAs: v });
        },
      };
    },
    {
      name: "monitoring-auth",
      storage: createJSONStorage(() => capacitorStorage),
      // Persist only what's safe to survive a reload. Cognito session is
      // handled by Amplify; sub/email/groups get re-hydrated by initialize().
      partialize: (state) => ({
        actingAs: state.actingAs,
        previewPersona: state.previewPersona,
        viewingAs: state.viewingAs,
      }),
    },
  ),
);
